import axios from 'axios';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';

import { normalizeC1Money } from '../../shared/money/normalizeC1';

import type {
  ApiError,
  ApiRequestConfig,
  StoredUserRecord,
  UserSummaryRecord,
  WorkspaceRecord
} from './apiTypes';

// The frontend talks to the versioned API surface. Every backend endpoint
// (all CRUD, every context) is mounted under /api/v1/; v1 reads return the
// ratified contract (money as C1 objects, ISO-8601-UTC-Z datetimes). The C1
// money objects are flattened back to the frontend's number model by the
// response interceptor below (see shared/money/normalizeC1). v0 == the legacy
// root-alias surface, kept for rollback. Bump this one constant to re-pin.
const API_VERSION_PREFIX = '/api/v1';

const resolveBaseURL = (): string => {
  const raw = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return `${raw}${API_VERSION_PREFIX}`;
};

const BASE_URL = resolveBaseURL();

// Axios defaults to `timeout: 0` — no timeout at all. On a link that DROPS
// rather than refuses, such a request never settles: it holds one of the
// browser's ~6 connections-per-host until the socket itself gives up (~300s in
// Chrome). Six of those and the app is frozen with no error and no spinner
// that ever resolves — the single most dangerous property this client had for
// a field worker whose call drops ten times in ten minutes.
//
// 45s is deliberately generous: long enough that a slow-but-working request on
// a village connection still completes, short enough that a dead one gives its
// connection slot back in seconds rather than minutes. Byte-carrying calls
// (uploads) override this per-call — see UPLOAD_TIMEOUT_MS.
const DEFAULT_TIMEOUT_MS = 45000;

// The refresh must resolve quickly or not at all: everything behind a 401 is
// queued on it. Timing out is now harmless — a refresh that never reached the
// server no longer destroys the session (see the refresh catch below).
const REFRESH_TIMEOUT_MS = 20000;

// Uploads carry bytes, so they get a far longer leash than a JSON read. A
// downscaled field photo is a few hundred KB — minutes at 200kbps — but this
// still bounds the hang.
export const UPLOAD_TIMEOUT_MS = 300000;

// Synchronous LLM generation is a different class of request again: a
// newsletter, grant draft or agent turn legitimately runs far longer than
// any CRUD read. Applying the 45s default to these would turn working calls
// into failures, so they declare their own budget rather than the default
// being stretched to cover the slowest thing the app does.
export const GENERATION_TIMEOUT_MS = 180000;

const apiClient = axios.create({
  baseURL: BASE_URL || undefined,
  timeout: DEFAULT_TIMEOUT_MS
});
const refreshClient = axios.create({
  baseURL: BASE_URL || undefined,
  timeout: REFRESH_TIMEOUT_MS
});
const retryClient = axios.create({
  baseURL: BASE_URL || undefined,
  timeout: DEFAULT_TIMEOUT_MS
});

/**
 * True when a request never got an answer from the server: timeout, connection
 * reset, DNS failure, TLS failure, or the device being offline.
 *
 * The distinction is load-bearing, not cosmetic. "The server said no" and "we
 * could not reach the server" demand opposite responses — the first means act
 * on the server's decision, the second means the decision is simply unknown
 * and the safe move is to change nothing and let the caller retry.
 */
export const isNetworkLevelFailure = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;
  if (!axiosError) return false;
  if (axiosError.response) return false;
  return Boolean(axiosError.request) || axiosError.code === 'ECONNABORTED';
};

/**
 * True when the request was given up on for taking too long, as opposed to
 * being actively refused. Worth separating in the UI: "this is taking longer
 * than usual" invites a retry; "could not connect" invites checking the link.
 */
export const isTimeoutFailure = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;
  return (
    axiosError?.code === 'ECONNABORTED' || axiosError?.code === 'ETIMEDOUT'
  );
};

const LOGIN_PATH = '/identity/login/';
const REFRESH_PATH = '/identity/token/refresh/';
let pendingRefresh: Promise<string | null> | null = null;

// Set on the first 401 with code='token_not_valid' in a burst. Suppresses
// per-error toasts + skips refresh attempts for every subsequent 401 in
// the same burst, then redirects once. Reset on next page load.
let _sessionDead = false;
const _handleSessionDead = () => {
  if (_sessionDead) return;
  _sessionDead = true;
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('token_refresh');
    localStorage.removeItem('user');
    localStorage.removeItem('user_summary');
  } catch {
    /* storage may throw in private mode — redirect anyway */
  }
  toast.error('Session expired, please log in again.', {
    icon: '⚠️',
    toastId: 'session-dead'
  });
  if (
    typeof window !== 'undefined' &&
    !window.location.pathname.startsWith('/identity/login')
  ) {
    window.location.replace('/identity/login');
  }
};

// ── Connectivity tracking ────────────────────────────────────────
// When the server is unreachable, suppress per-request toasts and
// surface a single persistent banner instead.
// ── Rate-limit tracking ─────────────────────────────────────────
// When a 429 is received, suppress duplicate toasts and expose the
// cooldown window so callers can skip requests entirely.
let _rateLimitedUntil = 0;
let _rateLimitToastShown = false;

export const isRateLimited = (): boolean => Date.now() < _rateLimitedUntil;

/**
 * A 401 that is about to be transparently refreshed-and-retried is transient
 * noise, not a user-facing error — the global error toast must be suppressed
 * for it. On the post-login dashboard fan-out, every authed request 401s in
 * the same tick when the access token has expired, then all recover on a
 * single (single-flighted) token refresh; without this guard the user sees a
 * wall of "Something went wrong" toasts that vanish a second later.
 *
 * True only when: it's a 401, on a non-auth (not login/refresh) request, not
 * already retried once, and a refresh token exists to retry with. If any of
 * those is false the toast should show as normal (e.g. no refresh token = a
 * genuinely dead/absent session, which _handleSessionDead handles elsewhere).
 */
export const isTransientRetriableAuthError = (params: {
  status?: number;
  isLoginRequest: boolean;
  isRefreshRequest: boolean;
  alreadyRetried: boolean;
  hasRefreshToken: boolean;
}): boolean =>
  params.status === 401 &&
  !params.isLoginRequest &&
  !params.isRefreshRequest &&
  !params.alreadyRetried &&
  params.hasRefreshToken;

const SAFE_METHODS = new Set(['get', 'head', 'options']);

/**
 * Is this an authorization REFUSAL of a read, rather than a failure?
 *
 * 401/403 on a safe method means "you may not see this" — the server decided
 * correctly and nothing went wrong, so a red "Something went wrong" is a false
 * statement. A page that asks a handful of questions the viewer is not entitled
 * to answers with a stack of red toasts, which is how people learn to ignore
 * toasts entirely.
 *
 * This suppresses a TOAST, it does not swallow the error: the promise still
 * rejects and the caller can render a denied or empty state in place. The real
 * fix for a refused read is always to stop asking.
 *
 * Unsafe methods are deliberately excluded — the user took an action and is
 * owed an answer about why it did not happen.
 */
export const isExpectedReadRefusal = (params: {
  status?: number;
  method?: string;
}): boolean =>
  (params.status === 401 || params.status === 403) &&
  SAFE_METHODS.has(String(params.method || 'get').toLowerCase());

let _networkErrorCount = 0;
let _isOffline = false;
let _offlineListeners: Array<(offline: boolean) => void> = [];
let _offlineSince = 0;
let _onlineDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const OFFLINE_THRESHOLD = 2; // consecutive network errors before showing overlay
const MIN_OFFLINE_DURATION_MS = 5000; // stay offline at least 5s to prevent flicker
const ONLINE_DEBOUNCE_MS = 3000; // wait 3s of successful requests before dismissing

const _notifyOfflineListeners = () => {
  _offlineListeners.forEach((fn) => {
    try {
      fn(_isOffline);
    } catch {
      /* noop */
    }
  });
};

const _markOffline = () => {
  if (_onlineDebounceTimer) {
    clearTimeout(_onlineDebounceTimer);
    _onlineDebounceTimer = null;
  }
  if (_isOffline) return;
  _isOffline = true;
  _offlineSince = Date.now();
  toast.dismiss();
  _notifyOfflineListeners();
};

const _markOnline = () => {
  _networkErrorCount = 0;
  if (!_isOffline) return;

  // Ensure minimum offline duration to prevent flicker
  const elapsed = Date.now() - _offlineSince;
  if (elapsed < MIN_OFFLINE_DURATION_MS) {
    if (!_onlineDebounceTimer) {
      _onlineDebounceTimer = setTimeout(() => {
        _onlineDebounceTimer = null;
        if (_networkErrorCount === 0) {
          _isOffline = false;
          _notifyOfflineListeners();
        }
      }, MIN_OFFLINE_DURATION_MS - elapsed);
    }
    return;
  }

  // Debounce: wait for sustained connectivity before dismissing
  if (_onlineDebounceTimer) return;
  _onlineDebounceTimer = setTimeout(() => {
    _onlineDebounceTimer = null;
    if (_networkErrorCount === 0) {
      _isOffline = false;
      _notifyOfflineListeners();
    }
  }, ONLINE_DEBOUNCE_MS);
};

const _recordNetworkError = () => {
  _networkErrorCount += 1;
  // Cancel any pending "go online" timer
  if (_onlineDebounceTimer) {
    clearTimeout(_onlineDebounceTimer);
    _onlineDebounceTimer = null;
  }
  if (_networkErrorCount >= OFFLINE_THRESHOLD) {
    _markOffline();
  }
};

/**
 * Ask the server one cheap question, purely to find out whether the link is
 * back. Used by the connectivity overlay's "Check again" so the operator has a
 * non-destructive way out: the previous only-action was
 * `window.location.reload()`, which on a page holding an unsent update threw
 * the work away to answer a question a 2KB request can answer.
 *
 * Uses its own instance against the UNVERSIONED health path (the liveness view
 * is mounted at /api/health/, outside /api/v1/) and reports into the same
 * online/offline counters the main client feeds.
 */
const probeClient = axios.create({ timeout: 10000 });

export const probeConnectivity = async (): Promise<boolean> => {
  const root = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  try {
    await probeClient.get(`${root}/api/health/`);
    _markOnline();
    return true;
  } catch (error) {
    if (isNetworkLevelFailure(error)) {
      _recordNetworkError();
      return false;
    }
    // The server answered — even a 4xx/5xx proves the link is up, which is the
    // only thing this probe is asking about.
    _markOnline();
    return true;
  }
};

export const subscribeConnectivity = (listener: (offline: boolean) => void) => {
  _offlineListeners.push(listener);
  // immediately notify current state
  listener(_isOffline);
  return () => {
    _offlineListeners = _offlineListeners.filter((fn) => fn !== listener);
  };
};

export const getIsOffline = () => _isOffline;

type GenericRecord = Record<string, unknown>;

const isAuthPath = (url: unknown, path: string): boolean => {
  if (typeof url !== 'string') return false;
  return url.includes(path);
};

const isCanceledRequest = (error: unknown): boolean => {
  const maybeError = error as { code?: string; name?: string };
  return (
    axios.isCancel?.(error) ||
    maybeError?.code === 'ERR_CANCELED' ||
    maybeError?.name === 'CanceledError' ||
    maybeError?.name === 'AbortError'
  );
};

const isPlainObject = (value: unknown): value is GenericRecord =>
  Boolean(value) &&
  typeof value === 'object' &&
  (value.constructor === Object ||
    Object.getPrototypeOf(value) === Object.prototype);

const readActiveWorkspaceId = (): string | number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const rawSummary = localStorage.getItem('user_summary');
    if (rawSummary) {
      const summary = JSON.parse(rawSummary) as UserSummaryRecord;
      const workspaceContext =
        summary?.workspace_context ||
        summary?.workspaceContext ||
        summary?.data?.workspace_context ||
        summary?.data?.workspaceContext ||
        null;
      const workspaces =
        summary?.workspaces ||
        summary?.data?.workspaces ||
        workspaceContext?.workspaces ||
        null;

      const availableIds = Array.isArray(workspaces)
        ? workspaces
            .map((workspace: WorkspaceRecord | null | undefined) => {
              if (!workspace || typeof workspace !== 'object') return null;
              return (
                workspace.id ||
                workspace.pk ||
                workspace.workspace_id ||
                workspace.uuid ||
                null
              );
            })
            .filter(Boolean)
            .map((value) => String(value))
        : [];

      const activeId = workspaceContext?.active_workspace_id
        ? String(workspaceContext.active_workspace_id)
        : null;

      if (
        activeId &&
        (availableIds.length === 0 || availableIds.includes(activeId))
      ) {
        return activeId;
      }

      if (availableIds.length > 0) {
        return availableIds[0];
      }
    }

    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      const parsed = JSON.parse(rawUser) as StoredUserRecord;
      return (
        parsed?.active_workspace_id ||
        parsed?.default_workspace_id ||
        parsed?.active_workspace ||
        parsed?.active_seed_id ||
        parsed?.default_seed_id ||
        null
      );
    }

    return null;
  } catch {
    return null;
  }
};

const normalizeWorkspaceParam = <T>(container: T): T => {
  if (!container || typeof container !== 'object') return container;
  const mutable = container as Record<string, unknown>;
  if (mutable.workspace_id === undefined && mutable.seed_id !== undefined) {
    mutable.workspace_id = mutable.seed_id;
    delete mutable.seed_id;
  }
  return container;
};

const isAbsoluteUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const normalizeApiPathname = (pathname: string): string => {
  if (!pathname || typeof pathname !== 'string') return pathname;
  let next = pathname;

  next = `/${next}`.replace(/^\/+/, '/').replace(/\/{2,}/g, '/');

  next = next.replace(/^\/seed(?=\/|$)/, '/workspaces');
  next = next.replace(/^\/seeds(?=\/|$)/, '/workspaces');
  next = next.replace(/^\/team\/seed(?=\/|$)/, '/team/workspaces');
  next = next.replace(
    /^\/notifications\/preferences\/seeds(?=\/|$)/,
    '/notifications/preferences/workspaces'
  );
  next = next.replace(
    /^\/workspaces\/categories\/subcategories(?=\/|$)/,
    '/workspaces/categories-subcategories'
  );
  next = next.replace(
    /^\/seed\/categories-subcategories(?=\/|$)/,
    '/workspaces/categories-subcategories'
  );
  next = next.replace(
    /^\/seed\/categories\/subcategories(?=\/|$)/,
    '/workspaces/categories-subcategories'
  );
  next = next.replace(
    /^\/sponsorship\/children(?=\/|$)/,
    '/sponsorship/recipients'
  );
  next = next.replace(
    /^\/sponsorship\/sponsor\/child(?=\/|$)/,
    '/sponsorship/sponsor/recipient'
  );
  next = next.replace(
    /^\/budget\/transaction\/child(?=\/|$)/,
    '/budget/transaction/recipient'
  );
  next = next.replace(/^\/project\/seed(?=\/|$)/, '/project/workspaces');
  next = next.replace(/^\/users(?=\/|$)/, '/identity');

  if (next === '/workspaces') {
    next = '/workspaces/';
  }

  return next;
};

const normalizeApiUrl = (url: unknown): unknown => {
  if (!url || typeof url !== 'string') return url;

  if (isAbsoluteUrl(url)) {
    try {
      const parsed = new URL(url);
      parsed.pathname = normalizeApiPathname(parsed.pathname);
      return parsed.toString();
    } catch {
      return url;
    }
  }

  return normalizeApiPathname(url);
};

const resolvePathname = (url: unknown): string => {
  if (!url || typeof url !== 'string') return '';
  if (isAbsoluteUrl(url)) {
    try {
      return new URL(url).pathname || '';
    } catch {
      return '';
    }
  }
  return url;
};

const shouldEnsureWorkspaceParam = (pathname: string): boolean =>
  pathname.startsWith('/membership/members/') ||
  pathname.startsWith('/ai/actions/metrics/') ||
  pathname.startsWith('/membership/invitations/') ||
  pathname.startsWith('/feature-flags/');

const attachRequestInterceptor = (client: typeof apiClient): void => {
  client.interceptors.request.use(
    async (config) => {
      if (typeof config?.url === 'string') {
        config.url = normalizeApiUrl(config.url) as string;
      }

      const resolvedPathname = resolvePathname(config?.url);

      if (config?.params) {
        normalizeWorkspaceParam(config.params);
      }

      if (shouldEnsureWorkspaceParam(resolvedPathname)) {
        const params = isPlainObject(config.params) ? config.params : {};
        if (!params.workspace_id) {
          const activeWorkspaceId = readActiveWorkspaceId();
          if (activeWorkspaceId) {
            params.workspace_id = activeWorkspaceId;
          }
        }
        config.params = params;
      }

      const data = config?.data;
      if (data) {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          try {
            const hasWorkspace = data.has('workspace_id');
            const seedValue = data.get('seed_id');
            if (
              !hasWorkspace &&
              seedValue !== null &&
              seedValue !== undefined
            ) {
              data.append('workspace_id', seedValue);
              if (typeof data.delete === 'function') {
                data.delete('seed_id');
              }
            }
          } catch {
            // no-op
          }
        } else if (isPlainObject(data)) {
          normalizeWorkspaceParam(data);
        }
      }

      const accessToken = localStorage.getItem('token');
      if (!config.headers) {
        config.headers = {};
      }
      if (!('Authorization' in config.headers)) {
        if (accessToken) {
          (
            config.headers as Record<string, string>
          ).Authorization = `Bearer ${accessToken}`;
        } else {
          delete (config.headers as Record<string, string>).Authorization;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

attachRequestInterceptor(apiClient);
attachRequestInterceptor(retryClient);

axios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    if (typeof config?.url === 'string') {
      config.url = normalizeApiUrl(config.url) as string;
    }
    if (config?.params) {
      normalizeWorkspaceParam(config.params);
    }

    const resolvedPathname = resolvePathname(config?.url);
    if (
      resolvedPathname.startsWith('/membership/members/') ||
      resolvedPathname.startsWith('/ai/actions/metrics/') ||
      resolvedPathname.startsWith('/membership/invitations/')
    ) {
      const params = isPlainObject(config.params) ? config.params : {};
      if (!params.workspace_id) {
        const activeWorkspaceId = readActiveWorkspaceId();
        if (activeWorkspaceId) {
          params.workspace_id = activeWorkspaceId;
        }
      }
      config.params = params;
    }

    const data = config?.data;
    if (data && isPlainObject(data)) {
      normalizeWorkspaceParam(data);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// v1 anti-corruption layer: flatten C1 money objects in the response body
// back to the frontend's number model before any caller sees them. Walks
// only object/array bodies; primitives (blobs, plain strings) pass through.
const normalizeResponseMoney = (response: { data?: unknown }) => {
  const { data } = response;
  if (data && typeof data === 'object') {
    normalizeC1Money(data);
  }
  return response;
};

apiClient.interceptors.response.use(
  (response) => {
    _markOnline();
    return normalizeResponseMoney(response);
  },
  async (error: ApiError) => {
    if (isCanceledRequest(error)) {
      console.info('Request canceled:', error?.message || error);
      return Promise.reject(error);
    }

    const requestUrl = error.config?.url;
    const requestPathname = resolvePathname(requestUrl || '');
    const isLoginRequest = isAuthPath(requestUrl, LOGIN_PATH);
    const isRefreshRequest = isAuthPath(requestUrl, REFRESH_PATH);
    // Suppress toasts for background/non-critical API calls that
    // fail silently (the caller handles the error locally).
    const isSilentPath =
      requestPathname.startsWith('/ai/agents/ai-config') ||
      // Teammate alias/profile is decorative; the fetch hook resolves a
      // default on 404 (no profile yet) and 403 (viewer role can't read
      // it — correct RBAC for sponsor/guest viewers). No global toast.
      requestPathname.startsWith('/ai/agents/teammate/') ||
      requestPathname.startsWith('/sponsor/methods') ||
      // Tenant theme is optional decoration — most hosts have no Theme
      // row, the service already returns null on failure, and the 404
      // was popping "Something went wrong" on the login page (the first
      // thing a demo visitor sees).
      requestPathname.startsWith('/landing/theme/') ||
      requestPathname.startsWith('/announcements/') ||
      // Sample-data mode is owner-gated (ADR 0010 D5) and its callers report their own
      // outcomes precisely — a refused enable is a documented result with a real
      // explanation ("this workspace has real donations"), not a generic failure. A
      // global "Something went wrong" on top of that is strictly worse information.
      requestPathname.startsWith('/sample-data/') ||
      requestPathname.startsWith('/feature-flags/') ||
      requestPathname.startsWith('/shop/') ||
      requestPathname.startsWith('/store/') ||
      // The workspace feed UI is shipped but the backend endpoint
      // (/social/workspaces/<ws>/feed/) doesn't exist yet. Suppress
      // the 404 toast until the feed endpoint lands or the UI is
      // removed. Tracked alongside the GTM scope freeze work.
      requestPathname.startsWith('/social/workspaces/') ||
      requestPathname.startsWith('/receipts/') ||
      requestPathname.startsWith('/documents/') ||
      requestPathname.includes('/groups/') ||
      requestPathname.includes('/permissions/') ||
      requestPathname.startsWith('/imports/') ||
      requestPathname.startsWith('/identity/logout') ||
      requestPathname.startsWith('/userpreferences') ||
      requestPathname.startsWith('/cart/list') ||
      // Message feedback (thumbs up/down) is best-effort — the
      // FeedbackButtons component handles its own rollback. Don't
      // pop the global error toast when the endpoint is unreachable
      // (e.g. backend not yet deployed / 404).
      /^\/ai\/conversations\/[^/]+\/messages\/[^/]+\/feedback\/?$/.test(
        requestPathname
      ) ||
      // Deep-run snapshot + events endpoints. The chat UI now
      // generates planId on submit so the WS subscription opens
      // before the run row is written; any caller that pings these
      // endpoints during that window legitimately gets 404. The hook
      // (useDeepRunProgress) gates the snapshot fetch on the first
      // WS event so this should rarely fire — but keep the toast
      // suppressed so a future caller can't accidentally regress
      // the UX. Stats endpoint is intentionally NOT in this group;
      // a 404 there is a real error.
      /^\/ai\/agents\/runs\/[a-f0-9-]{36}(\/events)?\/?$/.test(
        requestPathname
      ) ||
      // Invitation info + accept — ShareInvitationAcceptPage renders
      // a dedicated in-page error state ("Invitation no longer valid"
      // / inline form error), so the global toast is redundant noise.
      // Pending-share revoke (DELETE /sharing/invitations/<uuid>/) is
      // intentionally NOT in this group — that's a user-initiated
      // action where a toast IS the right failure signal.
      requestPathname === '/sharing/invitations/info/' ||
      requestPathname === '/sharing/invitations/accept/' ||
      // Starting a DM is refused for two knowable reasons (messaging
      // yourself; not sharing a workspace) and both callers — the contact
      // detail popup and the contacts card action — now render the server's
      // reason themselves. The global toast would only duplicate it.
      requestPathname === '/messaging/conversations/start/';
    const shouldShowGlobalToast =
      !isLoginRequest && !isRefreshRequest && !isSilentPath;

    if (!error.response) {
      console.error('Network Error:', error);
      _recordNetworkError();
      // When offline, the banner handles the messaging — no toast spam
      if (shouldShowGlobalToast && !_isOffline) {
        // Name the actual failure. "Unable to connect" is wrong and
        // unactionable when the truth is "your connection is slow and we gave
        // up waiting" — the operator's next move differs in each case.
        toast.error(
          isTimeoutFailure(error)
            ? 'That took too long on this connection. Please try again.'
            : 'Unable to connect to the server. Please check your connection.',
          {
            icon: '⚠️',
            toastId: 'network-error',
            autoClose: 5000
          }
        );
      }
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    console.error(`API Error (${status}):`, data);

    // Stale-token burst: localStorage holds an invalid JWT (signed with an
    // old SECRET_KEY, malformed, or for a deleted user). Every request in
    // the page paint will 401 with code='token_not_valid'. Refreshing
    // won't help — the access token isn't expired, it's invalid. Fire one
    // toast, clear the session, redirect; suppress everything else in the
    // burst so the user doesn't see a toast wall.
    if (status === 401 && (data as GenericRecord)?.code === 'token_not_valid') {
      _handleSessionDead();
      return Promise.reject(error);
    }
    if (_sessionDead) {
      return Promise.reject(error);
    }

    // Circuit breaker: 429 Too Many Requests — stop retrying, back off.
    // Suppress all toasts during the cooldown window to prevent toast storms.
    if (status === 429) {
      const retryAfter = error.response.headers?.['retry-after'];
      const waitSeconds = retryAfter ? parseInt(retryAfter, 10) : 60;
      console.warn(`Rate limited (429). Retry after ${waitSeconds}s.`);
      _rateLimitedUntil = Date.now() + waitSeconds * 1000;
      if (!_rateLimitToastShown) {
        _rateLimitToastShown = true;
        toast.warning?.('Too many requests. Please wait a moment.', {
          icon: '⏳',
          toastId: 'rate-limit',
          onClose: () => {
            _rateLimitToastShown = false;
          }
        });
      }
      return Promise.reject(error);
    }

    // Circuit breaker: if the current user's profile/detail returns 404,
    // the user no longer exists on this backend — force logout.
    const resolvedPathname = resolvePathname(requestUrl);
    if (
      status === 404 &&
      (resolvedPathname.startsWith('/identity/detail/') ||
        resolvedPathname.startsWith('/identity/users/') ||
        resolvedPathname.startsWith('/identity/me/'))
    ) {
      console.warn('User profile not found (404) — forcing session clear.');
      localStorage.removeItem('token');
      localStorage.removeItem('token_refresh');
      localStorage.removeItem('user');
      localStorage.removeItem('user_summary');
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/identity/login')
      ) {
        window.location.replace('/identity/login');
      }
      return Promise.reject(error);
    }

    // Feature-flag gate responses are explicit "this surface is off" —
    // not an error the user should see. The backend returns 403 with
    // detail="Feature not enabled." via RequiresFeatureFlag; suppress
    // the global toast. Callers that care can still handle the rejection.
    const isFeatureFlagGate =
      status === 403 &&
      (data as GenericRecord)?.detail === 'Feature not enabled.';

    // Membership-required gates fire when the viewer hits a workspace
    // they aren't a member of — e.g., opening a "sponsor a new seed"
    // flow on someone else's organization. The Layout still loads
    // member-only endpoints (members, transactions, imports) for that
    // workspace and each one 403s with error_code='WorkspaceMembership
    // RequiredError'. Until the sponsor/guest views learn to load a
    // reduced endpoint set, these 403s are expected — suppress the
    // global toast so the viewer doesn't see a stack of red errors.
    // The deep fix is for those page-loads to skip member-only fetches
    // when the viewer isn't a member; tracked separately.
    const isMembershipGate =
      status === 403 &&
      ((data as GenericRecord)?.error_code ===
        'WorkspaceMembershipRequiredError' ||
        (data as GenericRecord)?.error_code === 'TeamMembershipRequiredError');

    // 5xx responses count as "backend is unhealthy" just like a no-
    // response network error. Increment the same counter — once
    // OFFLINE_THRESHOLD consecutive failures land, _markOffline()
    // fires, ConnectivityBanner shows the sunset overlay, and
    // queued/in-flight toasts get dismissed. Without this, hooks
    // like useSeedSponsorshipPresentation that fire
    // addToast({ message: 'Server Error!' }) on 500 paint a wall
    // of red toasts instead of the calmer sunset state.
    if (typeof status === 'number' && status >= 500) {
      _recordNetworkError();
    }

    // Metered-AI limit (402). The workspace has spent its monthly AI-run
    // allowance for its tier (Free 20 / Pro 200 / Premium unlimited). The
    // backend returns 402 with {code, used, limit, upgrade_required}. Show a
    // tailored upgrade nudge instead of the generic "Something went wrong",
    // and emit an app event so a richer upgrade modal can hook in later.
    const isAiRunLimit =
      status === 402 &&
      (data as GenericRecord)?.code === 'ai_run_limit_exceeded';
    if (isAiRunLimit) {
      const used = (data as GenericRecord)?.used;
      const limit = (data as GenericRecord)?.limit;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('wanjala:ai-run-limit', { detail: { used, limit } })
        );
      }
      if (shouldShowGlobalToast && !_isOffline) {
        toast.warning?.(
          `You've used your monthly AI runs (${used}/${limit}). ` +
            'Upgrade your plan to keep using AI.',
          { icon: '⭐', toastId: 'ai-run-limit' }
        );
      }
      return Promise.reject(error);
    }

    // Transient expired-access-token 401s are about to be refreshed-and-
    // retried by the block below (single-flight via pendingRefresh). On the
    // post-login dashboard fan-out EVERY authed call 401s in the same tick,
    // then all recover on one refresh — so firing the global toast here
    // paints a wall of "Something went wrong" that vanishes a second later.
    // Suppress the toast for a 401 that HAS a refresh token and hasn't been
    // retried yet; if the refresh ultimately fails, _handleSessionDead()
    // owns that UX. (token_not_valid 401s already returned above.)
    const willRetryAuthError = isTransientRetriableAuthError({
      status,
      isLoginRequest,
      isRefreshRequest,
      alreadyRetried: Boolean((error.config as ApiRequestConfig)?._retry),
      hasRefreshToken: Boolean(localStorage.getItem('token_refresh'))
    });

    // Signout race: logout clears both tokens while dashboard queries are
    // still in flight; each one lands as a 401 a moment later. A 401 with
    // NO stored session (neither access nor refresh token) is the expected
    // outcome of a deliberate sign-out or an anonymous visitor hitting an
    // authed endpoint — the redirect logic below owns that UX, not a
    // "Something went wrong" toast.
    const isSignedOutAuthError =
      status === 401 &&
      !localStorage.getItem('token') &&
      !localStorage.getItem('token_refresh');

    // A REFUSAL IS NOT A FAILURE.
    //
    // 401/403 on a read means "you may not see this" — an authorization
    // outcome the server decided correctly. Nothing went wrong, so
    // "Something went wrong" is a false statement, and a stack of red toasts
    // for questions the UI should not have asked is how people learn to
    // ignore toasts entirely. The two gates above (`isFeatureFlagGate`,
    // `isMembershipGate`) were this same rule discovered twice and written as
    // payload-shape special cases; this states it once, by HTTP semantics,
    // instead of growing a third.
    //
    // This SUPPRESSES A TOAST, IT DOES NOT SWALLOW THE ERROR: the promise
    // still rejects, the caller still sees it and can render a denied or
    // empty state in place, and the server still logs the refusal. The real
    // fix for a refused read is always to stop asking — which is why the
    // sample-data owner gate and the invitations workspace-id were fixed in
    // the same change rather than just being muted here.
    //
    // Unsafe methods keep toasting: the user took an action and is owed an
    // answer about why it did not happen.
    const isReadRefusal = isExpectedReadRefusal({
      status,
      method: (error.config as ApiRequestConfig)?.method
    });

    if (
      shouldShowGlobalToast &&
      !_isOffline &&
      !isRateLimited() &&
      !isFeatureFlagGate &&
      !isMembershipGate &&
      !isReadRefusal &&
      !willRetryAuthError &&
      !isSignedOutAuthError
    ) {
      // Several endpoints report their reason under `error` rather than
      // `message` (the messaging refusals, for one). Reading only `message`
      // turned every one of them into a bare "Something went wrong", hiding a
      // reason the user could have acted on.
      const message =
        ((data as GenericRecord)?.message as string) ||
        ((data as GenericRecord)?.error as string) ||
        'Something went wrong';
      toast.error(message, { icon: '⚠️' });
    }

    if (status === 401 && !isLoginRequest && !isRefreshRequest) {
      const originalRequest = error.config as ApiRequestConfig;
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const refreshToken = localStorage.getItem('token_refresh');
        if (!refreshToken) {
          // Only a visitor who HAD a session gets the session-expired
          // treatment. An anonymous visitor (no tokens at all — e.g. a donor
          // opening a public /join, /sponsor or /forms link) hitting an
          // authenticated endpoint is a plain 401, NOT a dead session:
          // redirecting them to /identity/login would yank every anonymous
          // donor off the public donate surfaces.
          if (localStorage.getItem('token')) {
            _handleSessionDead();
          }
          return Promise.reject(error);
        }
        try {
          if (!pendingRefresh) {
            pendingRefresh = refreshClient
              .post(REFRESH_PATH, { refresh: refreshToken })
              .then((res) => {
                if (res.status === 200 && res.data.access) {
                  localStorage.setItem('token', res.data.access);
                  return res.data.access as string;
                }
                return null;
              })
              .catch((refreshError) => {
                // A refresh can fail for two completely different reasons and
                // this branch used to treat them identically, which is how a
                // single dropped packet on a Ugandan link deleted a still-valid
                // session and hard-navigated away from unsaved work.
                //
                //  * The SERVER answered and rejected the token (401/403) —
                //    the session really is dead; clear it and send them to
                //    login.
                //  * There was NO response at all (timeout, reset, DNS, TLS,
                //    offline) — we learned nothing about the session. The
                //    refresh token is still on the device and, because
                //    ROTATE_REFRESH_TOKENS is off server-side, it is still
                //    valid and still re-usable. Keep it. The caller gets a
                //    retriable network error, and the next request tries the
                //    refresh again.
                if (isNetworkLevelFailure(refreshError)) {
                  console.warn(
                    'Token refresh could not reach the server; keeping the session.',
                    (refreshError as AxiosError)?.code || 'no-response'
                  );
                  return null;
                }
                console.error(
                  'Token refresh rejected by server:',
                  refreshError
                );
                _handleSessionDead();
                return null;
              })
              .finally(() => {
                pendingRefresh = null;
              });
          }
          const newToken = await pendingRefresh;
          if (newToken) {
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }
            (
              originalRequest.headers as Record<string, string>
            ).Authorization = `Bearer ${newToken}`;
            return retryClient.request(originalRequest);
          }
        } catch {
          // refresh already handled above
        }
      }
    }

    return Promise.reject(error);
  }
);

// Retried requests (after a 401 -> token refresh) return through retryClient,
// bypassing apiClient's success interceptor, so apply the same C1 money
// normalization here. Otherwise a response that happened to be retried would
// leak C1 objects to callers.
retryClient.interceptors.response.use(
  (response) => normalizeResponseMoney(response),
  (error) => Promise.reject(error)
);

export default apiClient;
